import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import ComplianceInfoEditModal from '@/temu-platform/modules/compliance/ComplianceInfoEditModal';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import SyncPlatformJsonModal from '@/temu-platform/components/SyncPlatformJsonModal';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { extractTemuList, pickField } from '@/temu-platform/utils/temuResponse';
import { loadProductMetaBySkc } from '@/temu-platform/utils/temuProductMetaBySkc';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import { showToast } from '@/utils/toast';
import {
  COMPLIANCE_INFO_NAME_OPTIONS,
  COMPLIANCE_TYPE_OPTIONS,
  INFO_STATUS_TABS,
  emptyInfoDimensionStats,
  infoStatusDisplay,
} from '@/temu-platform/modules/compliance/complianceStatusEnums';
import SearchableSelect from '@/temu-platform/modules/products/components/SearchableSelect';
import '@/styles/temu-platform/temu-product-ops.css';

const SYNC_KINDS = ['compliance'];

const STATUS_ALL = 'all';
const STATUS_TABS = INFO_STATUS_TABS;

const ID_TYPE_OPTIONS = [
  { value: 'spu', label: 'SPU' },
  { value: 'skc', label: 'SKC' },
  { value: 'title', labelKey: 'compliance.lbTitle' },
];

const TABLE_COLUMNS = [
  { key: 'check', label: '', colClassName: 'tpo-certs-col tpo-certs-col--check', width: '50px' },
  { key: 'product', labelKey: 'orderList.productInfo', width: '30%' },
  { key: 'items', labelKey: 'compliance.infoColItems', width: '28%' },
  { key: 'status', labelKey: 'shop.colStatus', width: '16%' },
  /* 末列 width:auto 吸收余量，避免 table-layout:fixed 把多余宽度摊到复选列 */
  { key: 'ops', labelKey: 'certLib.colOps', colClassName: 'tpo-certs-col tpo-certs-col--ops tpo-certs-col--ops-flex' },
];

/** 状态来自商品合规信息（metadata.complianceInfoList / infoStatus） */
function buildComplianceItems(row) {
  const list = Array.isArray(row?.complianceInfoList) ? row.complianceInfoList : [];
  if (list.length) {
    return list.map((item, idx) => {
      const type = Number(item?.complianceType) || 0;
      const name = String(item?.name || item?.desc || i18n.t('compliance.infoItem', { ns: 'workbench', n: type || idx + 1 })).trim();
      const disp = infoStatusDisplay(item?.status);
      return {
        key: `meta-${type || idx}`,
        name,
        label: disp.label || '—',
        tone: disp.tone || 'muted',
      };
    });
  }
  const disp = infoStatusDisplay(pickField(row, 'infoStatus'));
  if (disp.code) {
    return [{ key: 'agg', name: i18n.t('compliance.infoName', { ns: 'workbench' }), label: disp.label, tone: disp.tone }];
  }
  return [{ key: 'empty', name: i18n.t('compliance.infoNone', { ns: 'workbench' }), label: '—', tone: 'muted' }];
}

function rowKey(row, index) {
  return String(pickField(row, 'productId', 'spu', 'skc', 'skcName', 'id') || `row-${index}`).trim();
}

function rowSpu(row, meta, fallbackKey) {
  return String(
    meta?.spuName
    || pickField(row, 'spu', 'spuName', 'productId')
    || fallbackKey
    || '',
  ).trim();
}

export default function ComplianceInfoPage() {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, concreteScopeId } = useTemuPageShopScope();
  const shopId = concreteScopeId;
  const [platformJsonOpen, setPlatformJsonOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [idTypeDraft, setIdTypeDraft] = useState('spu');
  const [idValueDraft, setIdValueDraft] = useState('');
  const [idTypeQuery, setIdTypeQuery] = useState('spu');
  const [idValueQuery, setIdValueQuery] = useState('');
  const [complianceTypeDraft, setComplianceTypeDraft] = useState('');
  const [complianceInfoDraft, setComplianceInfoDraft] = useState('');
  const [complianceTypeQuery, setComplianceTypeQuery] = useState('');
  const [complianceInfoQuery, setComplianceInfoQuery] = useState('');
  const [batchOpen, setBatchOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState(() => emptyInfoDimensionStats());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [productMeta, setProductMeta] = useState({});
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [editTarget, setEditTarget] = useState(null);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();
  const syncing = isBusy('compliance', shopId);

  useEffect(() => {
    setPage(1);
    setSelectedKeys(new Set());
    setComplianceTypeDraft('');
    setComplianceInfoDraft('');
    setComplianceTypeQuery('');
    setComplianceInfoQuery('');
  }, [shopId]);

  const buildListParams = useCallback((pageNo, size) => {
    const tokens = parseBatchTokens(idValueQuery).slice(0, BATCH_QUERY_LIMIT);
    const params = {
      source: 'cached',
      pageNum: pageNo,
      pageSize: size,
    };
    if (statusFilter && statusFilter !== STATUS_ALL) {
      params.infoStatus = statusFilter;
    }
    if (complianceTypeQuery) {
      const typeNum = Number(complianceTypeQuery);
      if (Number.isFinite(typeNum) && typeNum > 0) {
        params.complianceType = typeNum;
      }
    }
    if (complianceInfoQuery) {
      params.complianceInfo = complianceInfoQuery;
    }
    if (tokens.length) {
      if (idTypeQuery === 'title') {
        params.keyword = tokens[0];
      } else {
        const first = tokens[0];
        const asNum = Number(first);
        if (Number.isFinite(asNum) && asNum > 0) {
          params.productId = asNum;
          params.skcName = first;
        } else {
          params.keyword = first;
        }
        if (tokens.length > 1) {
          params.keyword = tokens.join(',');
        }
      }
    }
    return params;
  }, [statusFilter, idTypeQuery, idValueQuery, complianceTypeQuery, complianceInfoQuery]);

  const load = useCallback(async () => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      setStatusCounts(emptyInfoDimensionStats());
      setProductMeta({});
      return;
    }
    setLoading(true);
    try {
      const res = await temuPlatformAPI.listComplianceSkc(shopId, buildListParams(page, pageSize));
      const list = extractTemuList(res) || [];
      setRows(list);
      setTotal(Number(res?.total) || Number(res?.totalElements) || list.length);
      const counts = emptyInfoDimensionStats();
      const rawCounts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : {};
      Object.keys(counts).forEach((key) => {
        if (key === 'all') return;
        counts[key] = Number(rawCounts[key]) || 0;
      });
      counts.all = Number(res?.totalAll) || Object.keys(counts)
        .filter((k) => k !== 'all')
        .reduce((sum, k) => sum + (Number(counts[k]) || 0), 0);
      setStatusCounts(counts);
      if (res?.syncedAt || res?.lastSyncedAt) {
        setPersistedSyncedAt(String(res.syncedAt || res.lastSyncedAt));
      }
      const skcs = list.map((row) => rowKey(row)).filter((k) => k && !k.startsWith('row-'));
      if (skcs.length) {
        const meta = await loadProductMetaBySkc(shopId, skcs, { enrichCategoryPath: true });
        setProductMeta(meta);
      } else {
        setProductMeta({});
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      showToast(e?.message || t('compliance.infoLoadFail'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, buildListParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'compliance',
        shopId,
        label: t('compliance.infoSync'),
        silent: true,
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      setLastSyncedAt(new Date().toISOString());
      await load();
      showToast(result.view?.message || t('compliance.infoSyncDone'), 'success');
    } catch (e) {
      showToast(e?.message || t('productAction.syncFailed'), 'error');
    }
  }, [shopId, startJob, onTrackDone, load, t]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncingBusy = syncingLocal || syncing;

  const latestSyncedLabel = useMemo(() => {
    const ts = lastSyncedAt || persistedSyncedAt;
    return ts ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(ts) }) : t('tiktokCatalog.notSynced');
  }, [lastSyncedAt, persistedSyncedAt, t]);

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index)).filter((k) => k && !k.startsWith('row-')),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };
  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onQuery = () => {
    setIdTypeQuery(idTypeDraft);
    setIdValueQuery(idValueDraft.trim());
    setComplianceTypeQuery(String(complianceTypeDraft || '').trim());
    setComplianceInfoQuery(String(complianceInfoDraft || '').trim());
    setPage(1);
    setSelectedKeys(new Set());
  };

  const onReset = () => {
    setIdTypeDraft('spu');
    setIdValueDraft('');
    setIdTypeQuery('spu');
    setIdValueQuery('');
    setComplianceTypeDraft('');
    setComplianceInfoDraft('');
    setComplianceTypeQuery('');
    setComplianceInfoQuery('');
    setStatusFilter(STATUS_ALL);
    setPage(1);
    setSelectedKeys(new Set());
  };

  const writeExportCsv = (exportRows, kind) => {
    if (!exportRows.length) {
      showToast(t('compliance.infoNoneExport'), 'warning');
      return;
    }
    downloadCsv(
      `temu-compliance-info-${kind}-${Date.now()}.csv`,
      ['productId', 'title', 'infoStatus', 'complianceItems', 'lastSyncedAt'],
      exportRows.map((row, index) => {
        const key = rowKey(row, index);
        const meta = productMeta[key] || {};
        const items = buildComplianceItems(row);
        return {
          productId: key,
          title: meta.title || pickField(row, 'title', 'productTitle') || '',
          infoStatus: pickField(row, 'infoStatus') || '',
          complianceItems: items.map((it) => `${it.name}:${it.label}`).join('；'),
          lastSyncedAt: pickField(row, 'lastSyncedAt') || '',
        };
      }),
    );
    showToast(t('compliance.infoExported', { count: exportRows.length }), 'success');
  };

  const exportPage = () => writeExportCsv(rows, 'page');
  const exportSelected = () => {
    const selected = rows.filter((row, index) => selectedKeys.has(rowKey(row, index)));
    if (!selected.length) {
      showToast(t('compliance.infoPickExport'), 'warning');
      return;
    }
    writeExportCsv(selected, 'selected');
  };
  const exportFiltered = async () => {
    if (!shopId) return;
    setExporting(true);
    try {
      const collected = [];
      let pageNo = 1;
      const size = 100;
      while (pageNo <= 50) {
        const res = await temuPlatformAPI.listComplianceSkc(shopId, buildListParams(pageNo, size));
        const list = extractTemuList(res) || [];
        collected.push(...list);
        const totalCount = Number(res?.total) || Number(res?.totalElements) || collected.length;
        if (!list.length || collected.length >= totalCount || list.length < size) break;
        pageNo += 1;
      }
      writeExportCsv(collected, 'filtered');
    } catch (e) {
      showToast(e?.message || t('compliance.euExportFail'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const onBatchDelete = async () => {
    if (!shopId || selectedKeys.size === 0) {
      showToast(t('compliance.infoPickDelete'), 'warning');
      return;
    }
    const productIds = Array.from(selectedKeys)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!productIds.length) {
      showToast(t('compliance.infoMissingPid'), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('compliance.infoDeleteMsg', { count: productIds.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeleteComplianceSkc(shopId, { productIds });
      const deleted = Number(res?.deleted);
      showToast(
        deleted > 0 ? t('compliance.lbDeleted', { count: deleted }) : t('compliance.infoNoneDeleted'),
        deleted > 0 ? 'success' : 'warning',
      );
      setSelectedKeys(new Set());
      await load();
    } catch (e) {
      showToast(e?.message || t('shop.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchEdit = () => {
    if (selectedKeys.size !== 1) {
      showToast(t('compliance.infoOneOnly'), 'warning');
      return;
    }
    const key = Array.from(selectedKeys)[0];
    const row = rows.find((r, index) => rowKey(r, index) === key);
    const meta = productMeta[key] || {};
    setEditTarget({
      productId: Number(key) || key,
      title: meta.title || pickField(row, 'title', 'productTitle') || '',
    });
  };

  const handleSeedDemo = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('compliance.infoDemoTitle'),
      message: t('compliance.infoDemoMsg'),
      tone: 'default',
      confirmText: t('compliance.lbWriteDemo'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setSeedingDemo(true);
    try {
      const res = await temuPlatformAPI.seedComplianceInfoDemo(shopId);
      const count = Array.isArray(res?.skcNames) ? res.skcNames.length : (Number(res?.cacheUpserted) || 0);
      const coverage = Array.isArray(res?.statusCoverage) ? res.statusCoverage.length : 0;
      showToast(
        count > 0
          ? (coverage
            ? t('compliance.infoSeededCoverage', { count, kinds: coverage })
            : t('compliance.infoSeeded', { count }))
          : t('compliance.lbDemoWritten'),
        'success',
      );
      setPage(1);
      setStatusFilter(STATUS_ALL);
      setIdTypeDraft('spu');
      setIdValueDraft('');
      setIdTypeQuery('spu');
      setIdValueQuery('');
      setComplianceTypeDraft('');
      setComplianceInfoDraft('');
      setComplianceTypeQuery('');
      setComplianceInfoQuery('');
      setSelectedKeys(new Set());
      await load();
    } catch (e) {
      showToast(e?.message || t('compliance.lbLoadDemoFail'), 'error');
    } finally {
      setSeedingDemo(false);
    }
  }, [shopId, load, t]);

  const idTypeOption = ID_TYPE_OPTIONS.find((o) => o.value === idTypeDraft);
  const idTypeLabel = idTypeOption?.labelKey ? t(idTypeOption.labelKey) : (idTypeOption?.label || 'SPU');

  const statusTabs = (
    <div className="tpo-status-tabs" role="tablist" aria-label={t('compliance.infoAria')}>
      {STATUS_TABS.map((tab) => {
        const active = statusFilter === tab.id;
        const count = Number(statusCounts[tab.id]) || 0;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}`}
            onClick={() => {
              setStatusFilter(tab.id);
              setPage(1);
              setSelectedKeys(new Set());
            }}
          >
            {tab.id === 'all' ? t('tiktokCatalog.all') : t('compliance.info_' + tab.id, { defaultValue: tab.label })}
            {count > 0 ? (
              <span className="tpo-status-tab__count">{count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <TemuSyncProgressSlot kinds={SYNC_KINDS} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncingBusy || loading || deleting || exporting || seedingDemo}
            syncKinds={SYNC_KINDS}
            quotaKind="compliance"
            onSync={onSync}
            syncing={syncingBusy}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncingBusy}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('compliance.infoSyncHint')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || deleting}
            trailingExtra={(
              <button
                type="button"
                className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                disabled={!shopId}
                title={shopId
                  ? t('compliance.lbViewJson')
                  : t('syncedLib.pickConcrete')}
                onClick={() => setPlatformJsonOpen(true)}
              >
                {t('compliance.lbPlatformJson')}
              </button>
            )}
            extraActions={[
              {
                key: 'batch-edit',
                label: selectedKeys.size === 1 ? t('compliance.infoBatchEdit1') : t('compliance.infoBatchEdit'),
                disabled: !shopId || selectedKeys.size !== 1 || deleting || seedingDemo,
                onClick: onBatchEdit,
              },
              {
                key: 'seed-demo',
                label: seedingDemo ? t('compliance.lbWriting') : t('compliance.lbLoadDemo'),
                disabled: !shopId || seedingDemo || syncingBusy || loading || deleting,
                onClick: handleSeedDemo,
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch">
              <select
                className="tpo-select tpo-select--compact"
                value={idTypeDraft}
                disabled={!shopId}
                onChange={(e) => setIdTypeDraft(e.target.value)}
                aria-label={t('compliance.idType')}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : opt.label}</option>
                ))}
              </select>
              <input
                className="tpo-input"
                value={idValueDraft}
                placeholder={t('tiktokCatalog.idPlaceholder', { label: idTypeLabel })}
                disabled={!shopId}
                onChange={(e) => setIdValueDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInput', { label: idTypeLabel })}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchOpen(true)}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tpo-field tpo-field--inline tpo-field--status">
              <span className="tpo-field__label">{t('compliance.infoType')}</span>
              <select
                className="tpo-select tpo-select--status"
                value={complianceTypeDraft}
                disabled={!shopId}
                onChange={(e) => {
                  const next = e.target.value;
                  setComplianceTypeDraft(next);
                  const hit = COMPLIANCE_TYPE_OPTIONS.find((o) => o.value === next);
                  if (hit?.value) {
                    setComplianceInfoDraft(hit.label);
                  } else if (!next) {
                    setComplianceInfoDraft('');
                  }
                }}
                aria-label={t('compliance.infoType')}
              >
                {COMPLIANCE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.value ? (opt.labelKey ? t(opt.labelKey) : opt.label) : t('tiktokCatalog.all')}</option>
                ))}
              </select>
            </div>
            <div className="tpo-field tpo-field--inline tpo-field--w240">
              <span className="tpo-field__label">{t('compliance.infoName')}</span>
              <SearchableSelect
                value={complianceInfoDraft}
                disabled={!shopId}
                placeholder={!shopId ? t('returnList.pickShop') : t('shop.pleaseSelect')}
                options={COMPLIANCE_INFO_NAME_OPTIONS.filter((o) => o.value).map((o) => ({ ...o, label: o.labelKey ? t(o.labelKey) : o.label }))}
                allowClear
                onChange={(v) => setComplianceInfoDraft(v ? String(v) : '')}
              />
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" disabled={!shopId} onClick={onReset}>
              {t('shop.reset')}
            </button>
            <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('shop.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={stackShopDimensionTabs(shopDimensionTabs, statusTabs)}
        loading={Boolean(shopId) && loading}
        loadingLabel={t('compliance.infoQuery')}
      >
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-certs-table">
            <colgroup>
              {TABLE_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  className={col.colClassName || undefined}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('compliance.infoSelectAll')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {TABLE_COLUMNS.filter((c) => c.key !== 'check').map((col) => (
                  <th key={col.key} scope="col" className={`tpo-certs-th tpo-certs-th--${col.key}`}>
                    {col.labelKey ? t(col.labelKey) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">
                        {!shopId ? t('returnList.pickShop') : loading ? t('returnList.loading') : t('compliance.infoEmpty')}
                      </p>
                      {!shopId || loading ? null : (
                        <p className="tpo-certs-empty__desc">
                          {t('compliance.infoEmptyPull')}
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : rows.map((row, index) => {
                const key = rowKey(row, index);
                const meta = productMeta[key] || {};
                const title = meta.title || pickField(row, 'title', 'productTitle') || '';
                const imageUrl = meta.imageUrl || pickField(row, 'imageUrl', 'mainImageUrl') || '';
                const spu = rowSpu(row, meta, key);
                const items = buildComplianceItems(row);
                return (
                  <tr key={`${key}-${index}`}>
                    <td className="tpo-certs-td tpo-certs-td--check" onClick={() => toggleOne(key)}>
                      <input
                        type="checkbox"
                        aria-label={t('orderList.selectRow', { title: spu || key })}
                        checked={selectedKeys.has(key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleOne(key)}
                      />
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--product">
                      <div className="tpo-photo-product">
                        {imageUrl ? (
                          <TemuProductThumb
                            imageUrl={imageUrl}
                            className="tpo-photo-product__img"
                            placeholderClassName="tpo-photo-product__img tpo-photo-product__img--empty"
                            alt={title || spu || key}
                          />
                        ) : (
                          <div className="tpo-photo-product__img tpo-photo-product__img--empty" />
                        )}
                        <div className="tpo-photo-product__meta">
                          <HoverCopyField value={title} className="tpo-photo-product__title" popoverCopy />
                          {spu ? (
                            <HoverCopyField label="SPU" value={spu} className="tpo-photo-product__skc" separator=": " />
                          ) : null}
                          <div
                            className="tpo-photo-product__category tpo-muted"
                            title={meta.categoryPathLabel || meta.productTypeId || undefined}
                          >
                            {t('compliance.lbCategory', { name: meta.categoryPathLabel || meta.productTypeId || '—' })}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--wrap">
                      <ul className="tpo-compliance-info-list">
                        {items.map((item) => (
                          <li key={item.key} className="tpo-compliance-info-list__item">
                            <span className={`tpo-compliance-info-list__dot tpo-compliance-info-list__dot--${item.tone}`} />
                            <span className="tpo-compliance-info-list__name">{item.name}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--wrap">
                      <ul className="tpo-compliance-info-list tpo-compliance-info-list--status-only">
                        {items.map((item) => (
                          <li key={`${item.key}-status`} className="tpo-compliance-info-list__item">
                            <span className={`tpo-compliance-info-list__status tpo-compliance-info-list__status--${item.tone}`}>
                              {item.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--ops">
                      <button
                        type="button"
                        className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                        onClick={() => setEditTarget({ productId: Number(key) || key, title })}
                      >
                        {t('compliance.infoEdit')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('compliance.infoPager')}
        />
      </ProductListCard>

      <BatchQueryModal
        open={batchOpen}
        title={t('tiktokCatalog.batchInput', { label: idTypeLabel })}
        initialValue={idValueDraft}
        onClose={() => setBatchOpen(false)}
        onConfirm={(tokens) => setIdValueDraft(joinBatchTokens(tokens))}
      />
      <ComplianceInfoEditModal
        open={Boolean(editTarget)}
        shopId={shopId}
        productId={editTarget?.productId}
        productTitle={editTarget?.title || ''}
        onSaved={() => {
          setEditTarget(null);
          load();
        }}
        onClose={() => setEditTarget(null)}
      />
      <SyncPlatformJsonModal
        open={platformJsonOpen}
        shopId={shopId}
        dumpKind="compliance"
        title={t('compliance.infoJsonTitle')}
        hint={t('compliance.infoJsonHint')}
        onClose={() => setPlatformJsonOpen(false)}
      />
    </>
  );
}
